import { normalizeIssuerType } from "../reportMappers";

const EMPTY = "-";

export const displayValue = (value) => {
  if (value == null) return EMPTY;
  const text = String(value).trim();
  if (!text || text.toLowerCase() === "n/a" || text.toLowerCase() === "na") {
    return EMPTY;
  }
  return text;
};

const pad2 = (n) => String(n).padStart(2, "0");

export const formatDurationOpen = (startAt, endAt = new Date(), units = {}) => {
  if (!startAt) return EMPTY;
  const start = new Date(startAt);
  const end = endAt ? new Date(endAt) : new Date();
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return EMPTY;

  const hourUnit = units.hours || "h";
  const minuteUnit = units.minutes || "m";
  const diffMs = Math.max(0, end.getTime() - start.getTime());
  const totalMinutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours <= 0 && minutes <= 0) return `0${minuteUnit}`;
  if (hours <= 0) return `${minutes}${minuteUnit}`;
  return `${hours}${hourUnit} ${pad2(minutes)}${minuteUnit}`;
};

const resolveReporterName = (raw, lang) => {
  const issuer = raw?.issuer || raw?.user || {};
  if (lang === "ar") {
    return (
      issuer.full_name_ar ||
      issuer.name_ar ||
      issuer.name ||
      issuer.full_name_en ||
      EMPTY
    );
  }
  return (
    issuer.full_name_en ||
    issuer.name_en ||
    issuer.name ||
    issuer.full_name_ar ||
    EMPTY
  );
};

const resolveReporterRole = (raw, fallbackLabel) => {
  const issuer = raw?.issuer || raw?.user || {};
  const role =
    issuer.role_name ||
    issuer.role ||
    issuer.type ||
    raw?.role ||
    fallbackLabel ||
    "";
  return normalizeIssuerType(role) || EMPTY;
};

/**
 * Canonical issuer type for routing/permission checks — independent of the
 * displayed (localized) role label. Prefers `issuer.type`, which the API keeps
 * language-agnostic, before the localized `role_name`.
 */
const resolveReporterType = (raw, fallbackLabel) => {
  const issuer = raw?.issuer || raw?.user || {};
  const type =
    issuer.type ||
    issuer.role ||
    issuer.role_name ||
    raw?.role ||
    fallbackLabel ||
    "";
  return normalizeIssuerType(type);
};

const mediaPathExtension = (url) => {
  if (!url || typeof url !== "string") return "";
  const path = url.trim().split("?")[0].split("#")[0];
  const i = path.lastIndexOf(".");
  return i >= 0 ? path.slice(i).toLowerCase() : "";
};

const isRasterImageUrl = (url) => {
  const ext = mediaPathExtension(url);
  return (
    ext === ".png" ||
    ext === ".jpg" ||
    ext === ".jpeg" ||
    ext === ".gif" ||
    ext === ".webp" ||
    ext === ".bmp"
  );
};

const mediaFileName = (url, fallback) => {
  if (!url || typeof url !== "string") return fallback;
  const segment = url.trim().split("/").pop();
  return (segment && segment.split("?")[0]) || fallback;
};

const normalizeMediaItem = (item, index) => {
  if (!item) return null;
  if (typeof item === "string") {
    const url = item.trim();
    if (!url) return null;
    return {
      id: `${index}-${url}`,
      url,
      name: mediaFileName(url, `file-${index + 1}`),
      fileName: mediaFileName(url, `file-${index + 1}`),
      isImage: isRasterImageUrl(url),
    };
  }

  const url = String(
    item.url || item.image_url || item.upload_url || "",
  ).trim();
  if (!url) return null;
  const name =
    item.name ||
    item.file_name ||
    item.filename ||
    mediaFileName(url, `file-${index + 1}`);

  return {
    id: item.id || `${index}-${url}`,
    url,
    name,
    fileName: mediaFileName(url, name),
    isImage: isRasterImageUrl(url),
  };
};

const resolveMedia = (raw) => {
  let source = [];
  if (Array.isArray(raw?.pictures) && raw.pictures.length) {
    source = raw.pictures;
  } else if (Array.isArray(raw?.images) && raw.images.length) {
    source = raw.images;
  } else {
    const nested =
      raw?.logistics_issue_images || raw?.logistics_issue_images_attributes;
    if (Array.isArray(nested)) source = nested;
  }

  return source.map(normalizeMediaItem).filter(Boolean);
};

const resolveCommentDateTime = (comment) => {
  if (comment?.date || comment?.time) {
    return {
      date: displayValue(comment?.date),
      time: displayValue(comment?.time?.slice?.(0, 5) || comment?.time),
    };
  }

  const createdAt = comment?.created_at || comment?.createdAt;
  if (!createdAt) return { date: EMPTY, time: EMPTY };

  const parsed = new Date(createdAt);
  if (Number.isNaN(parsed.getTime())) return { date: EMPTY, time: EMPTY };

  const day = pad2(parsed.getDate());
  const month = pad2(parsed.getMonth() + 1);
  const year = parsed.getFullYear();

  return {
    date: `${day}-${month}-${year}`,
    time: `${pad2(parsed.getHours())}:${pad2(parsed.getMinutes())}`,
  };
};

const mapCommentItem = (comment) => {
  const name =
    comment?.user?.full_name_en ||
    comment?.user?.full_name_ar ||
    comment?.user?.name ||
    comment?.issuer?.name ||
    comment?.author_name ||
    comment?.company_user?.name ||
    comment?.name ||
    "";
  const author =
    comment?.user || comment?.issuer || comment?.company_user || {};
  const image =
    author.picture ||
    author.image ||
    author.avatar ||
    comment?.user_image ||
    comment?.image ||
    null;
  const { date, time } = resolveCommentDateTime(comment);
  const images =
    comment?.images ||
    comment?.logistics_issue_comment_images ||
    comment?.logistics_issue_comment_images_attributes ||
    [];

  return {
    id: comment?.id,
    name: displayValue(name),
    image,
    initials: String(name || "?")
      .trim()
      .slice(0, 2)
      .toUpperCase(),
    date,
    time,
    text: displayValue(
      comment?.description || comment?.body || comment?.comment,
    ),
    images: Array.isArray(images) ? images : [],
  };
};

/** Map a comments API list (or nested detail comments) for ReportView. */
export const mapLogisticsIssueComments = (list) => {
  if (!Array.isArray(list)) return [];
  return list.map(mapCommentItem);
};

const resolveComments = (raw) => {
  const nested =
    raw?.comments ||
    raw?.logistics_issue_comments ||
    raw?.logistics_issue_comments_attributes;
  return mapLogisticsIssueComments(nested);
};

/** Normalize admin/company logistics-issue detail into ReportView props. */
export const mapReportDetailToViewModel = (raw, { lang, labels } = {}) => {
  if (!raw) return null;

  const status = String(raw.status || "").toLowerCase();
  const isOpen = status === "open";
  const createdAt = raw.created_at || raw.reported_at;
  const closedAt = raw.closed_at;
  const date =
    raw.date ||
    (createdAt ? new Date(createdAt).toLocaleDateString("en-GB") : null);
  const time =
    raw.time?.slice?.(0, 5) ||
    (createdAt
      ? `${pad2(new Date(createdAt).getHours())}:${pad2(
          new Date(createdAt).getMinutes(),
        )}`
      : null);

  const media = resolveMedia(raw);

  const reportedDate = displayValue(
    typeof date === "string" && date.includes("-")
      ? date.split("-").length === 3 && date.split("-")[0].length === 4
        ? date.split("-").reverse().join("-")
        : date
      : date,
  );
  const reportedTime = displayValue(time);
  const reportedAt =
    reportedDate !== EMPTY && reportedTime !== EMPTY
      ? `${reportedDate} ,${reportedTime}`
      : reportedDate !== EMPTY
        ? reportedDate
        : reportedTime;

  let resolvedAt = EMPTY;
  if (!isOpen && closedAt) {
    const closed = new Date(closedAt);
    if (!Number.isNaN(closed.getTime())) {
      resolvedAt = `${pad2(closed.getDate())}-${pad2(
        closed.getMonth() + 1,
      )}-${closed.getFullYear()} ,${pad2(closed.getHours())}:${pad2(
        closed.getMinutes(),
      )}`;
    }
  }

  return {
    id: raw.id,
    title: displayValue(raw.title),
    status,
    isOpen,
    statusLabel:
      status === "closed"
        ? labels?.closed || "Closed"
        : labels?.open || "Open",
    reportIdLabel: raw.code || raw.reference || (raw.id ? `#${raw.id}` : EMPTY),
    priority: displayValue(raw.priority),
    eventName: displayValue(raw.event?.name || raw.event_name),
    zoneName: displayValue(raw.zone?.name || raw.zone_name),
    zoneSubLabel: displayValue(raw.zone?.building || raw.building_name),
    reportedDate,
    reportedTime,
    reportedAt: displayValue(reportedAt),
    resolvedAt: displayValue(resolvedAt),
    durationOpen: formatDurationOpen(
      createdAt,
      isOpen ? new Date() : closedAt || new Date(),
      { hours: labels?.durationHoursUnit, minutes: labels?.durationMinutesUnit },
    ),
    description: displayValue(raw.description),
    media,
    mediaCount: media.length,
    reporterName: displayValue(resolveReporterName(raw, lang)),
    reporterRole: displayValue(
      resolveReporterRole(raw, labels?.supervisor),
    ),
    reporterType: resolveReporterType(raw, labels?.supervisor),
    reporterImage:
      raw?.issuer?.picture ||
      raw?.issuer?.image ||
      raw?.user?.image ||
      null,
    reporterId: raw?.issuer?.id || raw?.user?.id || null,
    comments: resolveComments(raw),
    raw,
  };
};
